import React, { useEffect, useMemo } from "react"

import { useFormContext } from "react-hook-form"
import { usaStates } from "typed-usa-states"

import { AvailableInsuranceType } from "@common-shared/config/coverage.config"
import type { AvailablePlanType } from "@common-shared/config/coverage.config"

import { FRONT_REGEX_CONFIG } from "@front-shared/config/regex.config"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import { TagsSelectElement } from "@front-shared/elements/tagsSelect.element"
import { LookupService } from "@front-shared/services/lookup.service"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../config/coverage.config"
import { NETWORK_STATUS_CONFIG } from "../../../config/networkStatus.config"
import { RelativeDateRangeInputElement } from "../../../elements/relativeDateRangeInput.element"
import { LookupHelper } from "../../../helpers/lookup.helper"
import type { INetworkStatusRulesFilters } from "../../../types/networkStatus.types"

export const NetworkStatusRulesFiltersFormComponent: React.FunctionComponent<
    INetworkStatusRulesFiltersFormComponentProps
> = props => {
    const {
        register,
        setValue,
        control,
        watch,
        unregister,
        formState: { errors },
    } = useFormContext<INetworkStatusRulesFilters>()

    const dataWatcher = watch()

    // Set payer/TPA objects in form when reopening with applied filters (like policies)
    useEffect(() => {
        if (props.existingFilters.payer) {
            setValue("payer", props.existingFilters.payer)
        }

        if (props.existingFilters.thirdPartyAdministrator) {
            setValue(
                "thirdPartyAdministrator",
                props.existingFilters.thirdPartyAdministrator,
            )
        }
    }, [
        props.existingFilters.payer,
        props.existingFilters.thirdPartyAdministrator,
        setValue,
    ])

    const planTypeOptions = useMemo<ISelectRenderedOption[]>(() => {
        return LookupHelper.getRenderedOptionsFromObject<AvailablePlanType>(
            CHECKER_CONFIG.planTypeMapping,
        )
    }, [])

    const stateOptions = useMemo<ISelectRenderedOption[]>(() => {
        return usaStates.map((state, index) => ({
            key: `${state.abbreviation}-${index}`,
            value: state.abbreviation,
            displayValue: state.name,
            dropdownDisplayValue: state.name,
        }))
    }, [])

    return (
        <div className={props.className}>
            <div className="mt-[32px] grid grid-cols-2 grid-flow-row auto-cols-fr gap-x-4 gap-y-6">
                <div className="min-w-0">
                    <RelativeDateRangeInputElement
                        inputRootKey="startDate"
                        defaultValue={props.existingFilters.startDate}
                        placeholder="Select date(s)"
                        labelPrefix="Start date"
                        allowClear
                        pastDatesAllowed
                        futureDatesAllowed
                    />
                </div>

                <div className="min-w-0">
                    <RelativeDateRangeInputElement
                        inputRootKey="endDate"
                        defaultValue={props.existingFilters.endDate}
                        placeholder="Select date(s)"
                        labelPrefix="End date"
                        allowClear
                        pastDatesAllowed
                        futureDatesAllowed
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <SelectElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .networkStatus
                        }
                        name="networkStatus"
                        allowClear
                        reactHookFormRegister={register}
                        reactHookFormSet={setValue}
                        renderedOptions={Object.values(
                            COVERAGE_CONFIG.payerCoverageCheckNetworkMapped,
                        )}
                        defaultValue={
                            Object.values(
                                COVERAGE_CONFIG.payerCoverageCheckNetworkMapped,
                            ).find(
                                item =>
                                    item.value ===
                                    props.existingFilters.networkStatus,
                            )?.displayValue
                        }
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <InputElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .providerNpi
                        }
                        labelClassName="min-w-0 truncate"
                        isLabelStatic
                        placeholder={`10-digit NPI or ${NETWORK_STATUS_CONFIG.practiceWideProviderNpiSentinel}`}
                        name="providerNpi"
                        reactHookControl={control}
                        reactHookSetValue={setValue}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            validate: (value: string | undefined) => {
                                const trimmedValue = (value ?? "").trim()

                                if (!trimmedValue) {
                                    return true
                                }

                                if (
                                    trimmedValue.toUpperCase() ===
                                    NETWORK_STATUS_CONFIG.practiceWideProviderNpiSentinel
                                ) {
                                    return true
                                }

                                return FRONT_REGEX_CONFIG.npi.test(trimmedValue)
                                    ? true
                                    : "Use a 10-digit NPI or PRACTICE for practice-wide rules"
                            },
                        }}
                        defaultValue={props.existingFilters.providerNpi}
                        allowClear
                    />
                    <PrimaryText
                        typography="caption"
                        className="mt-[8px] block opacity-70"
                    >
                        {`Type ${NETWORK_STATUS_CONFIG.practiceWideProviderNpiSentinel} to list only ${NETWORK_STATUS_CONFIG.practiceWideProviderNpiLabel.toLowerCase()} rules. Use a 10-digit NPI to filter by provider (practice-wide rules are included).`}
                    </PrimaryText>
                </div>

                <div className="min-w-0 pt-[16px]">
                    <InputElement
                        type="number"
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .order
                        }
                        labelClassName="min-w-0 truncate"
                        isLabelStatic
                        name="order"
                        reactHookControl={control}
                        reactHookSetValue={setValue}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.existingFilters.order}
                        allowClear
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <SelectElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .state
                        }
                        name="state"
                        allowClear
                        reactHookFormRegister={register}
                        reactHookFormSet={setValue}
                        renderedOptions={stateOptions}
                        defaultValue={
                            stateOptions.find(
                                item =>
                                    item.value === props.existingFilters.state,
                            )?.displayValue
                        }
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <InputElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .planName
                        }
                        labelClassName="min-w-0 truncate"
                        name="planName"
                        isLabelStatic
                        reactHookControl={control}
                        reactHookSetValue={setValue}
                        defaultValue={props.existingFilters.planName}
                        allowClear
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <InputElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .groupName
                        }
                        labelClassName="min-w-0 truncate"
                        name="groupName"
                        isLabelStatic
                        reactHookControl={control}
                        reactHookSetValue={setValue}
                        defaultValue={props.existingFilters.groupName}
                        allowClear
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <AutoCompleteElement
                        name="payer"
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .payerId
                        }
                        placeholder="Search payers..."
                        dataFetcher={(search?: string) =>
                            LookupService.lookupPayers(search)
                        }
                        itemRenderer={LookupHelper.getRenderedPayer}
                        reactHookFormErrors={errors}
                        reactHookFormRegister={register}
                        reactHookFormUnregister={unregister}
                        reactHookFormSet={setValue}
                        debounceMilliseconds={250}
                        defaultValue={
                            props.existingFilters.payer
                                ? LookupHelper.getRenderedPayer(
                                      props.existingFilters.payer,
                                  ).displayValue
                                : undefined
                        }
                        isLabelStatic
                        ignoreAutoSelectionOnBlur
                        clearOnEmptySearch
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <AutoCompleteElement
                        name="thirdPartyAdministrator"
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .thirdPartyAdministratorId
                        }
                        placeholder="Search third party administrators..."
                        dataFetcher={(search?: string) =>
                            LookupService.lookupPayers(search)
                        }
                        itemRenderer={LookupHelper.getRenderedPayer}
                        reactHookFormErrors={errors}
                        reactHookFormRegister={register}
                        reactHookFormUnregister={unregister}
                        reactHookFormSet={setValue}
                        debounceMilliseconds={250}
                        defaultValue={
                            props.existingFilters.thirdPartyAdministrator
                                ? LookupHelper.getRenderedPayer(
                                      props.existingFilters
                                          .thirdPartyAdministrator,
                                  ).displayValue
                                : undefined
                        }
                        isLabelStatic
                        ignoreAutoSelectionOnBlur
                        clearOnEmptySearch
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <TagsSelectElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .insuranceTypes
                        }
                        name="insuranceTypes"
                        placeholder="Select as many as needed"
                        onSelected={(items: string[]) => {
                            setValue(
                                "insuranceTypes",
                                items as AvailableInsuranceType[],
                            )
                        }}
                        options={LookupHelper.getRawOptionsFromObjectMapped(
                            COVERAGE_CONFIG.availableInsuranceTypeMapping,
                            COVERAGE_CONFIG.unsupportedInsuranceTypes,
                        )}
                        externalValue={
                            dataWatcher.insuranceTypes ??
                            props.existingFilters.insuranceTypes
                        }
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <TagsSelectElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels
                                .planTypes
                        }
                        name="planTypes"
                        placeholder="Select as many as needed"
                        onSelected={(items: string[]) => {
                            setValue("planTypes", items as AvailablePlanType[])
                        }}
                        options={planTypeOptions.map(o => ({
                            label: o.displayValue ?? String(o.value),
                            value: o.value as string,
                        }))}
                        externalValue={
                            dataWatcher.planTypes ??
                            props.existingFilters.planTypes
                        }
                    />
                </div>

                <div className="min-w-0 pt-[16px]">
                    <InputElement
                        label={
                            NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels.name
                        }
                        labelClassName="min-w-0 truncate"
                        name="name"
                        isLabelStatic
                        reactHookControl={control}
                        reactHookSetValue={setValue}
                        defaultValue={props.existingFilters.name}
                        allowClear
                    />
                </div>
            </div>
        </div>
    )
}

interface INetworkStatusRulesFiltersFormComponentProps {
    className?: string
    existingFilters: INetworkStatusRulesFilters
}
